import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import { useDetailsModalSave } from '../../hooks/useDetailsModalSave';
import { EventApi } from '../../services/api/EventApi';
import '../details/DetailsSection.css';
import CalendarDayIcon from '../calendar/CalendarDayIcon';

export default function EventDetails({
  event,
  onClose,
  onUpdate,
  onSaved,
  onEdit,
  onDelete,
  endeavor,
  onGoToEndeavor
}) {
  const { deleteEventSeries, state, toggleAssignmentCompleted } = useData();
  const [editedData, setEditedData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const hasChanges = Object.keys(editedData).length > 0;

  // Use shared modal save hook with optimistic update + rollback
  const { handleSave: performSave, saving, error } = useDetailsModalSave({
    entity: event,
    entityType: 'event',
    buildPatch: (editedData, event) => {
      console.log('[EventDetails buildPatch] Starting:', {
        editedData,
        eventId: event?.eventId,
        hasStartDatetime: 'startDatetime' in editedData,
        hasEndDatetime: 'endDatetime' in editedData
      });
      
      // Merge edited fields with full event object
      const merged = { ...event };
      // Ensure eventId is always present for backend validation
      merged.eventId = event.eventId || event.id;
      
      if ('name' in editedData) {
        merged.name = editedData.name;
        merged.title = editedData.name;
      }
      if ('description' in editedData) merged.description = editedData.description;
      if ('location' in editedData) merged.location = editedData.location;
      
      // Convert datetime-local strings to backend format
      // Backend uses LocalDateTime (timezone-naive), so send time exactly as user entered it
      if ('startDatetime' in editedData) {
        if (editedData.startDatetime && typeof editedData.startDatetime === 'string') {
          // datetime-local input gives us "YYYY-MM-DDTHH:MM" format
          // Just append seconds to match backend's expected format
          merged.startDatetime = editedData.startDatetime.includes(':00:00') 
            ? editedData.startDatetime 
            : editedData.startDatetime + ':00';
          console.log('[EventDetails] startDatetime:', merged.startDatetime);
        } else {
          merged.startDatetime = editedData.startDatetime;
        }
      }
      
      if ('endDatetime' in editedData) {
        if (editedData.endDatetime && typeof editedData.endDatetime === 'string') {
          // Same treatment for end time
          merged.endDatetime = editedData.endDatetime.includes(':00:00') 
            ? editedData.endDatetime 
            : editedData.endDatetime + ':00';
          console.log('[EventDetails] endDatetime:', merged.endDatetime);
        } else {
          merged.endDatetime = editedData.endDatetime;
        }
      }
      
      console.log('[EventDetails buildPatch] Final merged result:', {
        eventId: merged.eventId,
        startDatetime: merged.startDatetime,
        endDatetime: merged.endDatetime,
        changedFields: Object.keys(editedData)
      });
      
      return merged;
    },
    apiUpdate: async (id, payload) => {
      return await EventApi.updateEvent(id, payload);
    },
    applyLocalUpdate: onUpdate,
    onSaved,
    onSuccess: () => {
      setEditingField(null);
      setEditedData({});
    }
  });

  const loading = saving;

  const eventDate = useMemo(() => event?.startDatetime || event?.eventDate || new Date(), [event?.startDatetime, event?.eventDate]);

  // Check if this is a recurring event
  // An event is part of a recurring series if it has ANY of these indicators:
  // - isRecurring flag (parent event)
  // - recurrenceRule (parent event with RRULE)
  // - parentEventId (child event that's part of a series)
  const isRecurringEvent = Boolean(
    event?.isRecurring || 
    event?.recurrenceRule || 
    event?.parentEventId
  );

  // Debug logging
  useEffect(() => {
    if (event) {
      console.log('[EventDetails] Event data:', {
        eventId: event.eventId || event.id,
        isRecurring: event.isRecurring,
        recurrenceRule: event.recurrenceRule,
        parentEventId: event.parentEventId,
        isRecurringEvent
      });
      console.log('[EventDetails] onDelete prop:', typeof onDelete, !!onDelete);
    }
  }, [event, isRecurringEvent, onDelete]);

  // Calculate derived properties
  const isPastEvent = useMemo(() => {
    if (!event) return false;
    const now = new Date();
    const end = event.endDatetime ? new Date(event.endDatetime) : new Date(eventDate);
    return end < now;
  }, [event, eventDate]);

  const eventInfo = useMemo(() => {
    if (!event) return {
      start: 'Unknown',
      end: 'Unknown',
      duration: 'Unknown',
      createdAt: 'Unknown',
      updatedAt: 'Unknown',
      status: 'Upcoming',
      typeName: 'Event'
    };
    
    const start = event.startDatetime
      ? new Date(event.startDatetime).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        })
      : 'Unknown';
    
    const end = event.endDatetime
      ? new Date(event.endDatetime).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        })
      : 'Unknown';

    // Calculate duration
    let duration = 'Unknown';
    if (event.startDatetime && event.endDatetime) {
      const startDate = new Date(event.startDatetime);
      const endDate = new Date(event.endDatetime);
      const diffMs = endDate - startDate;
      const diffMins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      if (hours > 0) {
        duration = mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      } else {
        duration = `${mins}m`;
      }
    }

    const createdAt = event.createdAt
      ? new Date(event.createdAt).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        })
      : 'Unknown';
    
    const updatedAt = event.updatedAt
      ? new Date(event.updatedAt).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        })
      : 'Unknown';

    const isCanceled = Boolean(event.canceled);
    let status = 'Upcoming';
    if (isCanceled) status = 'Canceled';
    else if (isPastEvent) status = 'Past';

    const typeName = event.eventType || 'Event';

    return { start, end, duration, createdAt, updatedAt, status, typeName };
  }, [event, isPastEvent]);

  // Read course location from endeavor metadata (fallback to endeavor.location)
  const endeavorLocation = (() => {
    const meta = Array.isArray(endeavor?.metadata) ? endeavor.metadata : [];
    const item = meta.find(m => (m?.metadataName || '').trim().toLowerCase() === 'course location');
    const val = (item?.value || '').toString().trim();
    return val || endeavor?.location || null;
  })();

  const typeEmoji = (() => {
    const t = (event.eventType || '').toLowerCase();
    if (t.includes('lecture') || t.includes('class')) return '🎓';
    if (t.includes('lab')) return '🔬';
    if (t.includes('exam')) return '📝';
    if (t.includes('quiz')) return '❓';
    if (t.includes('discussion')) return '💬';
    return null;
  })();

  const isCanceled = Boolean(event?.canceled);
  const isFromSyllabus = Boolean(event?.isFromSyllabus);
  const statusKey = isCanceled ? 'canceled' : (isPastEvent ? 'past' : 'upcoming');

  // Helper to convert date to local datetime-local format (not UTC)
  const toLocalDatetimeString = useCallback((dateString) => {
    if (!dateString) return '';
    
    // If it's already in datetime-local format (YYYY-MM-DDTHH:mm), return as-is
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Otherwise it's an ISO string, convert to local datetime format
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, []);

  // Helper to format day key for assignment filtering (YYYY-MM-DD in local time)
  const formatDayKey = useCallback((dateString) => {
    if (!dateString) return '';
    const dt = new Date(dateString);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  // Helper to format time (12-hour format)
  const formatTime = useCallback((dateString) => {
    if (!dateString) return 'No due time';
    return new Date(dateString).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }, []);

  // Get assignments for this event's course
  const courseAssignments = useMemo(() => {
    if (!event?.endeavorId || !state?.assignmentsById) return [];
    
    // Get all assignments from state and filter by endeavorId
    const allAssignments = Object.values(state.assignmentsById);
    return allAssignments.filter(a => {
      const aEndeavorId = a.endeavorId || a.endeavorID || a.endeavor_id;
      return aEndeavorId === event.endeavorId;
    });
  }, [event?.endeavorId, state?.assignmentsById]);

  // Filter assignments due on the same day as this event
  const assignmentsForDay = useMemo(() => {
    if (!event?.startDatetime || !courseAssignments.length) return [];
    
    const eventDayKey = formatDayKey(event.startDatetime);
    return courseAssignments.filter((a) => {
      const due = a.dueDatetime || a.dueDate;
      if (!due) return false;
      return formatDayKey(due) === eventDayKey;
    });
  }, [event?.startDatetime, courseAssignments, formatDayKey]);

  // Clear edited data when event prop changes (after save from parent)
  useEffect(() => {
    setEditedData({});
    setEditingField(null);
  }, [event]);

  // Early return after all hooks
  if (!event) return null;

  const startEditingField = (field, currentValue) => {
    setEditingField(field);
    if (!editedData[field]) {
      setEditedData(prev => ({ ...prev, [field]: currentValue }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const cancelEditing = () => {
    setEditingField(null);
    setEditedData({});
  };

  const saveChanges = async () => {
    if (!hasChanges) {
      return;
    }
    await performSave(editedData);
  };

  const updateField = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="event-modal-grid">
      <div className="event-modal-left">
        <div
          className={`event-combined ${isPastEvent ? 'past-event' : ''}`}
          data-status={statusKey}
          data-canceled={isCanceled || undefined}
          data-surface="event-details"
        >
      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-dialog">
            <h3>Delete Event{isRecurringEvent ? ' (Recurring)' : ''}?</h3>
            {isRecurringEvent ? (
              <>
                <p>This is a recurring event. What would you like to delete?</p>
                <div className="delete-confirmation-actions recurring-delete-options">
                  <button
                    className="btn-cancel"
                    onClick={() => setShowDeleteConfirm(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-delete-single"
                    onClick={async () => {
                      try {
                        await onDelete(event.eventId || event.id);
                        setShowDeleteConfirm(false);
                        if (onClose) onClose();
                      } catch (err) {
                        console.error('[EventDetails] Failed to delete event (recurring):', err);
                        setShowDeleteConfirm(false);
                      }
                    }}
                    type="button"
                  >
                    Delete This Event Only
                  </button>
                  <button
                    className="btn-delete-series"
                    onClick={async () => {
                      try {
                        const parentId = event.parentEventId || event.eventId || event.id;
                        const response = await deleteEventSeries(parentId);
                        if (!response.ok) {
                          throw new Error('Failed to delete series');
                        }
                        setShowDeleteConfirm(false);
                        if (onClose) onClose();
                      } catch (err) {
                        console.error('[EventDetails] Failed to delete series:', err);
                        setShowDeleteConfirm(false);
                      }
                    }}
                    type="button"
                  >
                    Delete Entire Series
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>Are you sure you want to permanently delete this event?</p>
                <div className="delete-confirmation-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => setShowDeleteConfirm(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-delete"
                    onClick={async () => {
                      try {
                        await onDelete(event.eventId || event.id);
                        setShowDeleteConfirm(false);
                        if (onClose) onClose();
                      } catch (err) {
                        console.error('[EventDetails] Failed to delete event:', err);
                        setShowDeleteConfirm(false);
                      }
                    }}
                    type="button"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {error && (
        <div style={{
          background: 'var(--intent-danger-bg)',
          color: 'var(--intent-danger-text)',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid var(--intent-danger-border)'
        }}>
          {error}
        </div>
      )}

      <div className="event-top-row">
        <div className="event-title-section">
          <div className="event-icon-large event-icon-with-calendar">
            <CalendarDayIcon date={eventDate} size={64} />
            {typeEmoji && <span className="event-type-overlay" title={event.eventType}>{typeEmoji}</span>}
          </div>
          <div className="event-title-meta">
            {editingField === 'name' ? (
              <input
                type="text"
                className="inline-edit-input title-input"
                value={editedData.name ?? event.name ?? event.title ?? ''}
                onChange={(e) => updateField('name', e.target.value)}
                autoFocus
                style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: '600',
                  width: '100%',
                  padding: '0.25rem 0.5rem',
                  border: '2px solid var(--semantic-primary)',
                  borderRadius: '4px'
                }}
              />
            ) : (
              <h2 
                className="event-title eventLeftTitle editable-field" 
                onClick={() => startEditingField('name', event.name || event.title)}
                title="Click to edit"
              >
                {editedData.name ?? event.name ?? event.title}
              </h2>
            )}
            <div className="event-meta">
              <span className="event-type chip" data-chip="type">{eventInfo.typeName}</span>
              {typeof event.sequence === 'number' && (
                <span className="event-type chip" data-chip="session" title="Session number">Session #{event.sequence}</span>
              )}
              {isCanceled && <span className="event-type chip" data-chip="canceled" title="This session is canceled">❌ Canceled</span>}
              {isFromSyllabus && <span className="event-type chip" data-chip="syllabus" title="Extracted from syllabus">🤖 From Syllabus</span>}
              {endeavor && onGoToEndeavor && (
                <span
                  className="event-endeavor chip clickable"
                  data-chip="course"
                  onClick={onGoToEndeavor}
                >
                  {endeavor.name}
                </span>
              )}
              {event.location && <span className="location-badge chip" data-chip="location">📍 {event.location}</span>}
              {endeavorLocation && endeavorLocation !== event.location && (
                <span className="location-badge chip" data-chip="course-location" title="Course location">🏫 {endeavorLocation}</span>
              )}
            </div>
          </div>
        </div>
        <div className="combined-actions">
          {hasChanges && (
            <button
              className="btn btn-primary"
              onClick={saveChanges}
              disabled={loading}
            >
              {loading ? 'Saving...' : '💾 Save'}
            </button>
          )}
        </div>
      </div>

      {editingField === 'description' ? (
        <textarea
          className="inline-edit-input description-input"
          value={editedData.description ?? event.description ?? ''}
          onChange={(e) => updateField('description', e.target.value)}
          autoFocus
          placeholder="Add description..."
          rows={4}
          style={{
            width: '100%',
            padding: '0.75rem',
            border: '2px solid var(--semantic-primary)',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.95rem',
            fontFamily: 'inherit'
          }}
        />
      ) : (
        <div 
          className={`event-description-block editable-field ${!event.description ? 'empty-field' : ''}`}
          onClick={() => startEditingField('description', event.description || '')}
          title="Click to edit"
          style={!event.description ? { fontStyle: 'italic', opacity: 0.6, padding: '0.75rem', cursor: 'pointer' } : { cursor: 'pointer' }}
        >
          {editedData.description ?? event.description ?? 'Click to add description...'}
        </div>
      )}

      <div className="event-details-grid">
        <div className="detail-item">
          <label>Start</label>
          {editingField === 'startDatetime' ? (
            <input
              type="datetime-local"
              className="inline-edit-input"
              value={toLocalDatetimeString(editedData.startDatetime ?? event.startDatetime)}
              onChange={(e) => {
                // Store the raw datetime-local string value (don't convert yet)
                updateField('startDatetime', e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{
                padding: '0.25rem 0.5rem',
                border: '2px solid var(--semantic-primary)',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
          ) : (
            <p 
              className={`editable-field ${isPastEvent ? 'past-text' : ''}`}
              onClick={() => startEditingField('startDatetime', event.startDatetime)}
              title="Click to edit"
            >
              {eventInfo.start}
            </p>
          )}
        </div>
        <div className="detail-item">
          <label>End</label>
          {editingField === 'endDatetime' ? (
            <input
              type="datetime-local"
              className="inline-edit-input"
              value={toLocalDatetimeString(editedData.endDatetime ?? event.endDatetime)}
              onChange={(e) => {
                // Store the raw datetime-local string value (don't convert yet)
                updateField('endDatetime', e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{
                padding: '0.25rem 0.5rem',
                border: '2px solid var(--semantic-primary)',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}
            />
          ) : (
            <p 
              className={`editable-field ${isPastEvent ? 'past-text' : ''}`}
              onClick={() => startEditingField('endDatetime', event.endDatetime)}
              title="Click to edit"
            >
              {eventInfo.end}
            </p>
          )}
        </div>
        <div className="detail-item"><label>Duration</label><p>{eventInfo.duration}</p></div>
        <div className="detail-item">
          <label>Status</label>
          <p>
            <span
              className="status-badge"
              data-status={statusKey}
            >
              {isCanceled ? 'Canceled' : eventInfo.status}
            </span>
          </p>
        </div>
        {typeof event.sequence === 'number' && (
          <div className="detail-item"><label>Session</label><p>#{event.sequence}</p></div>
        )}
        {event.location && (
          <div className="detail-item">
            <label>Location</label>
            {editingField === 'location' ? (
              <input
                type="text"
                className="inline-edit-input"
                value={editedData.location ?? event.location ?? ''}
                onChange={(e) => updateField('location', e.target.value)}
                autoFocus
                placeholder="Add location..."
                style={{
                  padding: '0.25rem 0.5rem',
                  border: '2px solid var(--semantic-primary)',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  width: '100%'
                }}
              />
            ) : (
              <p 
                className="editable-field"
                onClick={() => startEditingField('location', event.location)}
                title="Click to edit"
              >
                📍 {editedData.location ?? event.location}
              </p>
            )}
          </div>
        )}
        {endeavorLocation && endeavorLocation !== event.location && (
          <div className="detail-item"><label>Course Location</label><p>🏫 {endeavorLocation}</p></div>
        )}
        <div className="detail-item"><label>Created</label><p>{eventInfo.createdAt}</p></div>
        <div className="detail-item"><label>Updated</label><p>{eventInfo.updatedAt}</p></div>

        {(Array.isArray(event.topics) && event.topics.length > 0) && (
          <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
            <label>Topics</label>
            <p>{event.topics.join(' • ')}</p>
          </div>
        )}
        {(Array.isArray(event.readings) && event.readings.length > 0) && (
          <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
            <label>Readings</label>
            <p>{event.readings.join(' • ')}</p>
          </div>
        )}
      </div>
    </div>
      </div>

      <div className="event-modal-right">
        <AssignmentsPanel
          assignments={assignmentsForDay}
          formatTime={formatTime}
          toggleAssignmentCompleted={toggleAssignmentCompleted}
        />
      </div>
    </div>
  );
}

// AssignmentsPanel - inline component for assignment list
function AssignmentsPanel({ assignments, formatTime, toggleAssignmentCompleted }) {
  const [transitioningIds, setTransitioningIds] = React.useState(new Set());

  const handleToggle = async (assignment) => {
    if (typeof toggleAssignmentCompleted !== 'function') {
      console.warn('toggleAssignmentCompleted not available');
      return;
    }

    const id = assignment.assignmentId || assignment.id;
    
    // Mark as transitioning to prevent re-sort during animation
    setTransitioningIds(prev => new Set([...prev, id]));

    try {
      await toggleAssignmentCompleted(assignment, { source: 'event-modal' });
      
      // Keep in place for animation duration (1.2s total)
      setTimeout(() => {
        setTransitioningIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 1300);
    } catch (err) {
      console.error('Failed to toggle assignment:', err);
      // Remove from transitioning on error
      setTransitioningIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Sort assignments: incomplete first, then by due time ascending
  // But keep transitioning items in their current position
  const sortedAssignments = [...assignments].sort((a, b) => {
    const aId = a.assignmentId || a.id;
    const bId = b.assignmentId || b.id;
    const aTransitioning = transitioningIds.has(aId);
    const bTransitioning = transitioningIds.has(bId);
    
    // Keep transitioning items in place
    if (aTransitioning && !bTransitioning) return -1;
    if (!aTransitioning && bTransitioning) return 1;
    if (aTransitioning && bTransitioning) return 0;
    
    const aDone = !!(a.completed ?? a.isDone);
    const bDone = !!(b.completed ?? b.isDone);
    
    // Incomplete first
    if (aDone !== bDone) return aDone ? 1 : -1;
    
    // Then by due time ascending
    const aDue = a.dueDatetime || a.dueDate;
    const bDue = b.dueDatetime || b.dueDate;
    if (!aDue && !bDue) return 0;
    if (!aDue) return 1;
    if (!bDue) return -1;
    return new Date(aDue) - new Date(bDue);
  });

  // Split into incomplete and complete
  const incompleteAssignments = sortedAssignments.filter(a => !(a.completed ?? a.isDone));
  const completeAssignments = sortedAssignments.filter(a => !!(a.completed ?? a.isDone));

  const renderAssignment = (a) => {
    const id = a.assignmentId || a.id;
    const title = a.title || a.name;
    const isDone = !!(a.completed ?? a.isDone);
    const dueDate = a.dueDatetime || a.dueDate;

    const handleRowClick = (e) => {
      // Don't toggle if clicking on a link or the checkbox itself
      if (e.target.tagName === 'A' || e.target.closest('.eventAssignCircle')) return;
      handleToggle(a);
    };

    const handleKeyDown = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleToggle(a);
      }
    };

    return (
      <div 
        key={id} 
        className={`eventAssignRow ${isDone ? 'isDone' : ''}`}
        role="button"
        tabIndex={0}
        onClick={handleRowClick}
        onKeyDown={handleKeyDown}
      >
        <div className="eventAssignCheck">
          <div 
            className={`eventAssignCircle ${isDone ? 'checked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle(a);
            }}
          >
            {isDone && (
              <svg className="eventAssignCheckmark" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M20 6L9 17L4 12" 
                  stroke="currentColor" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span className="eventAssignTitle">{title}</span>
        </div>

        <div className="eventAssignMeta">
          <div className="eventAssignTime">
            {dueDate ? formatTime(dueDate) : 'No due time'}
          </div>
          <span className={`eventAssignPill ${isDone ? 'done' : 'todo'}`}>
            {isDone ? 'Done' : 'To do'}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="event-assign-panel">
      <div className="event-assign-header">
        <div className="event-assign-title">Assignments for this event</div>
        <div className="event-assign-count">{sortedAssignments.length}</div>
      </div>

      {sortedAssignments.length === 0 ? (
        <div className="eventAssignEmpty">
          <img
            className="eventAssignEmptyImg"
            src="/scout_images/scout_wave_new.png"
            alt="Scout waving"
          />
          <div className="eventAssignEmptyTitle">No assignments for this event.</div>
          <div className="eventAssignEmptySub">You're clear for this class session.</div>
        </div>
      ) : (
        <div className="event-assign-scroll">
          {incompleteAssignments.length > 0 && (
            <>
              {completeAssignments.length > 0 && (
                <div className="eventAssignSectionHeader">
                  <div className="eventAssignSectionTitle">To Do</div>
                  <div className="eventAssignSectionCount">{incompleteAssignments.length}</div>
                </div>
              )}
              {incompleteAssignments.map(renderAssignment)}
            </>
          )}

          {completeAssignments.length > 0 && (
            <>
              <div className="eventAssignSectionHeader">
                <div className="eventAssignSectionTitle">Complete</div>
                <div className="eventAssignSectionCount">{completeAssignments.length}</div>
              </div>
              {completeAssignments.map(renderAssignment)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
